import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  X,
  BookOpen,
  MessageSquare,
  Sparkles,
  Volume2,
  Gamepad2,
  ArrowRight,
  ExternalLink,
  Heart,
  Sliders,
  CheckCircle2
} from 'lucide-react';
import { playSound } from '../audio';

interface TrainingGuideModalProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenSMS?: () => void;
  onOpenAmawal?: () => void;
}

type GuideTab = 'KIDS' | 'ADULTS' | 'CUSTOMIZE';

export const TrainingGuideModal: React.FC<TrainingGuideModalProps> = ({
  isOpen,
  onClose,
  onOpenSMS,
  onOpenAmawal,
}) => {
  const [activeTab, setActiveTab] = useState<GuideTab>('KIDS');

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div
        className="fixed inset-0 z-[100001] flex items-center justify-center p-3 sm:p-5 bg-black/75 sm:backdrop-blur-sm overflow-y-auto"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.92, y: 16 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.94, y: 10 }}
          transition={{ type: 'spring', stiffness: 350, damping: 28 }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-xl bg-[#FBF5E2] border-2 border-amber-700/60 shadow-[0_10px_50px_rgba(0,0,0,0.4)] rounded-2xl sm:rounded-3xl p-5 sm:p-7 text-stone-900 flex flex-col gap-4 sm:gap-5 my-auto max-h-[92vh] overflow-y-auto select-text"
        >
          {/* Close button */}
          <button
            type="button"
            onClick={() => {
              playSound('click');
              onClose();
            }}
            aria-label="Fermer"
            className="absolute top-3.5 right-3.5 sm:top-4 sm:right-4 p-2 rounded-full bg-stone-300/60 hover:bg-stone-300 text-stone-700 hover:text-stone-950 transition-all cursor-pointer z-10"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Header Badge & Title */}
          <div className="flex flex-col items-center text-center gap-1.5 pt-1">
            <div className="flex items-center gap-2">
              <span className="text-2xl sm:text-3xl text-amber-700 font-black">
                ⵣ
              </span>
              <span className="px-3 py-0.5 rounded-full bg-amber-200/80 border border-amber-600/40 text-amber-950 text-[11px] sm:text-xs font-cinzel font-bold uppercase tracking-widest">
                Guide d'Apprentissage
              </span>
              <span className="text-2xl sm:text-3xl text-amber-700 font-black">
                ⵣ
              </span>
            </div>

            <h2 className="text-xl sm:text-2xl font-black font-cinzel text-amber-950 tracking-wide mt-1">
              Comment bien s'entraîner ?
            </h2>
            <p className="text-xs sm:text-sm text-amber-900 font-semibold">
              Des conseils ciblés et simples pour progresser en kabyle selon votre profil
            </p>
          </div>

          {/* Navigation Tabs - Clean 3-Tab Pill bar */}
          <div className="grid grid-cols-3 gap-1.5 p-1 bg-[#f0e4c3] rounded-xl border border-amber-600/30">
            <button
              type="button"
              onClick={() => {
                playSound('click');
                setActiveTab('KIDS');
              }}
              className={`py-2 px-1.5 sm:px-3 rounded-lg font-bold text-xs sm:text-sm flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-1.5 transition-all cursor-pointer text-center ${
                activeTab === 'KIDS'
                  ? 'bg-emerald-700 text-white shadow-xs border border-emerald-500'
                  : 'text-stone-700 hover:text-stone-950 hover:bg-[#e8dbb8]'
              }`}
            >
              <span className="text-base sm:text-lg">🧒</span>
              <span className="font-cinzel tracking-tight sm:tracking-normal">Enfants & Ados</span>
            </button>

            <button
              type="button"
              onClick={() => {
                playSound('click');
                setActiveTab('ADULTS');
              }}
              className={`py-2 px-1.5 sm:px-3 rounded-lg font-bold text-xs sm:text-sm flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-1.5 transition-all cursor-pointer text-center ${
                activeTab === 'ADULTS'
                  ? 'bg-amber-700 text-white shadow-xs border border-amber-500'
                  : 'text-stone-700 hover:text-stone-950 hover:bg-[#e8dbb8]'
              }`}
            >
              <span className="text-base sm:text-lg">🧑</span>
              <span className="font-cinzel tracking-tight sm:tracking-normal">Adultes</span>
            </button>

            <button
              type="button"
              onClick={() => {
                playSound('click');
                setActiveTab('CUSTOMIZE');
              }}
              className={`py-2 px-1.5 sm:px-3 rounded-lg font-bold text-xs sm:text-sm flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-1.5 transition-all cursor-pointer text-center ${
                activeTab === 'CUSTOMIZE'
                  ? 'bg-purple-800 text-white shadow-xs border border-purple-600'
                  : 'text-stone-700 hover:text-stone-950 hover:bg-[#e8dbb8]'
              }`}
            >
              <span className="text-base sm:text-lg">🎯</span>
              <span className="font-cinzel tracking-tight sm:tracking-normal">Sur-Mesure</span>
            </button>
          </div>

          {/* Core Content Area */}
          <div className="flex flex-col gap-3 sm:gap-3.5 text-xs sm:text-sm leading-relaxed text-stone-800">

            {/* TAB 1: ENFANTS & ADOS */}
            {activeTab === 'KIDS' && (
              <div className="space-y-3">
                {/* Adventure Mode Card */}
                <div className="p-3.5 sm:p-4 rounded-xl bg-[#f4ebd0] border border-amber-600/30 flex gap-3 items-start shadow-xs">
                  <div className="p-2 rounded-lg bg-emerald-200/80 border border-emerald-500/40 text-emerald-900 shrink-0 mt-0.5">
                    <Gamepad2 className="w-5 h-5 text-emerald-800" />
                  </div>
                  <div className="space-y-1">
                    <p className="font-bold text-amber-950 text-sm font-cinzel">
                      Mode Aventure & Quizz Énigmes
                    </p>
                    <ul className="space-y-1.5 text-stone-800">
                      <li className="flex items-start gap-2">
                        <span className="text-emerald-700 font-bold shrink-0 mt-0.5">•</span>
                        <span><strong>Progression étape par étape :</strong> Traverse les 5 villages du Djurdjura et découvre les mots essentiels du quotidien.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-emerald-700 font-bold shrink-0 mt-0.5">•</span>
                        <span><strong>Écoute & Répétition :</strong> Clique sur le bouton haut-parleur 🔊 pour écouter la prononciation et répète le mot à voix haute.</span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Rewards & Routine Card */}
                <div className="p-3.5 sm:p-4 rounded-xl bg-[#f4ebd0] border border-amber-600/30 flex gap-3 items-start shadow-xs">
                  <div className="p-2 rounded-lg bg-amber-200/80 border border-amber-500/40 text-amber-900 shrink-0 mt-0.5">
                    <Sparkles className="w-5 h-5 text-amber-800" />
                  </div>
                  <div className="space-y-1">
                    <p className="font-bold text-amber-950 text-sm font-cinzel">
                      Récompenses & Rythme Conseillé
                    </p>
                    <ul className="space-y-1.5 text-stone-800">
                      <li className="flex items-start gap-2">
                        <span className="text-amber-700 font-bold shrink-0 mt-0.5">•</span>
                        <span><strong>Héros & Skins :</strong> Chaque bonne réponse te rapporte des XP pour débloquer de nouvelles tenues dans l'<strong>Inventaire 🎒</strong>.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-amber-700 font-bold shrink-0 mt-0.5">•</span>
                        <span><strong>5 à 10 minutes par jour :</strong> Une courte session quotidienne suffit pour mémoriser durablement les animaux, couleurs, objets et chiffres.</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* TAB 2: ADULTES */}
            {activeTab === 'ADULTS' && (
              <div className="space-y-3">
                {/* Highlighted SMS Discussion Card */}
                <div className="p-3.5 sm:p-4 rounded-xl bg-gradient-to-br from-amber-100/90 via-[#f4ebd0] to-orange-100/80 border-2 border-amber-600/50 flex gap-3 items-start shadow-sm">
                  <div className="p-2 rounded-lg bg-amber-300/70 border border-amber-500/50 text-amber-950 shrink-0 mt-0.5">
                    <MessageSquare className="w-5 h-5 text-amber-900" />
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="px-2 py-0.5 rounded bg-amber-700 text-amber-50 text-[10px] font-black uppercase tracking-wider">
                        Recommandé 🔥
                      </span>
                      <p className="font-bold text-amber-950 text-sm font-cinzel">
                        Discussions SMS & Clavier Kabyle
                      </p>
                    </div>
                    <ul className="space-y-1 text-stone-800">
                      <li className="flex items-start gap-2">
                        <span className="text-amber-800 font-bold shrink-0 mt-0.5">•</span>
                        <span><strong>Situations authentiques :</strong> Échangez par messages (famille, marché, village, nouvelles).</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-amber-800 font-bold shrink-0 mt-0.5">•</span>
                        <span><strong>Clavier berbère intégré :</strong> Tapez directement avec les caractères spéciaux (<em>Ɛ, Ɣ, Ḍ, Ṣ...</em>) dans l'<strong>Inventaire 🎒</strong> pour automatiser vos réflexes d'écriture.</span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Amawal & Challenges Card */}
                <div className="p-3.5 sm:p-4 rounded-xl bg-[#f4ebd0] border border-amber-600/30 flex gap-3 items-start shadow-xs">
                  <div className="p-2 rounded-lg bg-amber-200/80 border border-amber-500/40 text-amber-900 shrink-0 mt-0.5">
                    <BookOpen className="w-5 h-5 text-amber-800" />
                  </div>
                  <div className="space-y-1">
                    <p className="font-bold text-amber-950 text-sm font-cinzel">
                      Dictionnaire Amawal, Mots Croisés & Contes
                    </p>
                    <ul className="space-y-1 text-stone-800">
                      <li className="flex items-start gap-2">
                        <span className="text-amber-700 font-bold shrink-0 mt-0.5">•</span>
                        <span><strong>Dictionnaire Amawal :</strong> Plus de 700 mots et proverbes classés par thèmes avec recherche rapide.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-amber-700 font-bold shrink-0 mt-0.5">•</span>
                        <span><strong>Mots Croisés & Contes :</strong> Testez votre mémoire lexicale et écoutez des légendes kabyles traditionnelles.</span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Quick actions for adults */}
                {(onOpenSMS || onOpenAmawal) && (
                  <div className="flex flex-wrap gap-2 pt-1 justify-center sm:justify-start">
                    {onOpenSMS && (
                      <button
                        type="button"
                        onClick={() => {
                          playSound('click');
                          onOpenSMS();
                        }}
                        className="px-3 py-1.5 rounded-lg bg-amber-700 hover:bg-amber-800 text-white text-xs font-bold flex items-center gap-1.5 shadow-xs transition-colors cursor-pointer"
                      >
                        <MessageSquare className="w-3.5 h-3.5" />
                        <span>Ouvrir les Discussions SMS</span>
                      </button>
                    )}
                    {onOpenAmawal && (
                      <button
                        type="button"
                        onClick={() => {
                          playSound('click');
                          onOpenAmawal();
                        }}
                        className="px-3 py-1.5 rounded-lg bg-[#e8dbb8] hover:bg-[#dfceaa] text-amber-950 text-xs font-bold flex items-center gap-1.5 border border-amber-600/40 transition-colors cursor-pointer"
                      >
                        <BookOpen className="w-3.5 h-3.5 text-amber-800" />
                        <span>Consulter l'Amawal</span>
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* TAB 3: CUSTOMIZE / SUR-MESURE */}
            {activeTab === 'CUSTOMIZE' && (
              <div className="space-y-3">
                {/* Freedom Card */}
                <div className="p-3.5 sm:p-4 rounded-xl bg-[#f4ebd0] border border-amber-600/30 flex gap-3 items-start shadow-xs">
                  <div className="p-2 rounded-lg bg-purple-200/80 border border-purple-500/40 text-purple-900 shrink-0 mt-0.5">
                    <Sliders className="w-5 h-5 text-purple-800" />
                  </div>
                  <div className="space-y-1">
                    <p className="font-bold text-amber-950 text-sm font-cinzel">
                      Liberté Totale & Personnalisation
                    </p>
                    <ul className="space-y-1.5 text-stone-800">
                      <li className="flex items-start gap-2">
                        <span className="text-purple-700 font-bold shrink-0 mt-0.5">•</span>
                        <span><strong>Aucun ordre imposé :</strong> Vous choisissez librement vos activités selon vos envies (Aventure, SMS, Mots Croisés, Dictionnaire).</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-700 font-bold shrink-0 mt-0.5">•</span>
                        <span><strong>Hub d'Entraînement :</strong> Tous les mini-jeux, contes et modules d'écriture sont accessibles directement dans l'<strong>Inventaire 🎒</strong>.</span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Progress & Save Card */}
                <div className="p-3.5 sm:p-4 rounded-xl bg-[#f4ebd0] border border-amber-600/30 flex gap-3 items-start shadow-xs">
                  <div className="p-2 rounded-lg bg-amber-200/80 border border-amber-500/40 text-amber-900 shrink-0 mt-0.5">
                    <CheckCircle2 className="w-5 h-5 text-amber-800" />
                  </div>
                  <div className="space-y-1">
                    <p className="font-bold text-amber-950 text-sm font-cinzel">
                      Sauvegarde Automatique de votre Progression
                    </p>
                    <ul className="space-y-1.5 text-stone-800">
                      <li className="flex items-start gap-2">
                        <span className="text-amber-700 font-bold shrink-0 mt-0.5">•</span>
                        <span><strong>Sauvegarde continue :</strong> Vos XP, mots découverts et skins débloqués sont automatiquement enregistrés sur votre appareil.</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

          </div>

          {/* Footer Action Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2 border-t border-amber-900/20">
            <div className="text-[11px] text-stone-600 text-center sm:text-left">
              <span>Retrouvez ce guide à tout moment via le bouton <strong>« Guide & Tutoriel »</strong>.</span>
            </div>

            <button
              type="button"
              onClick={() => {
                playSound('click');
                onClose();
              }}
              className="w-full sm:w-auto px-6 py-2.5 bg-gradient-to-r from-amber-700 via-amber-600 to-amber-700 hover:from-amber-600 hover:to-amber-600 text-amber-50 font-cinzel font-black text-sm sm:text-base rounded-xl shadow-[0_0_20px_rgba(180,83,9,0.3)] border border-amber-500 active:scale-95 transition-all cursor-pointer flex items-center justify-center gap-2 shrink-0"
            >
              <span>J'ai compris</span>
              <span className="text-amber-100 font-black">✓</span>
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
